import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Paperclip, X, FileText, Image as ImageIcon, DownloadSimple, Spinner } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,image/*,application/pdf";

export const fileHref = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

const isImage = (a) =>
  (a.content_type || "").startsWith("image/") ||
  /\.(jpe?g|png|webp|gif)$/i.test(a.name || "") ||
  /\.(jpe?g|png|webp|gif)$/i.test(a.url || "");

const humanSize = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Lets the user pick + upload files. Calls onChange with the full attachment array. */
export function AttachmentUploader({ value = [], onChange, testId = "attach" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    const added = [];
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/attachments/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        added.push(data);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "yüklenemedi"}`);
      }
    }
    setUploading(false);
    if (added.length) onChange?.([...value, ...added]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => onChange?.(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        data-testid={`${testId}-input`}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        data-testid={`${testId}-btn`}
      >
        {uploading ? <Spinner size={16} className="mr-1 animate-spin" /> : <Paperclip size={16} className="mr-1" />}
        {uploading ? "Yükleniyor…" : "Belge / Fotoğraf Ekle"}
      </Button>
      {value.length > 0 && (
        <div className="space-y-1.5" data-testid={`${testId}-list`}>
          {value.map((a, idx) => (
            <div key={idx} className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-sm" data-testid={`${testId}-item-${idx}`}>
              {isImage(a) ? <ImageIcon size={16} className="text-primary shrink-0" /> : <FileText size={16} className="text-primary shrink-0" />}
              <span className="truncate flex-1">{a.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{humanSize(a.size)}</span>
              <button type="button" onClick={() => remove(idx)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`${testId}-remove-${idx}`}>
                <X size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[11px] text-muted-foreground">Resim, PDF, Word, Excel, PowerPoint, TXT, CSV · en fazla 15MB</div>
    </div>
  );
}

/** Read-only display of attachments (image thumbnails + download links). */
export function AttachmentList({ attachments = [], testId = "attach-view" }) {
  if (!attachments || attachments.length === 0) return null;
  const images = attachments.filter(isImage);
  const files = attachments.filter((a) => !isImage(a));
  return (
    <div className="mt-3 space-y-2" data-testid={testId}>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((a, idx) => (
            <a
              key={idx}
              href={fileHref(a.url)}
              target="_blank"
              rel="noreferrer"
              className="block rounded-md overflow-hidden border border-border hover:ring-2 hover:ring-primary/40 transition"
              data-testid={`${testId}-img-${idx}`}
              title={a.name}
            >
              <img src={fileHref(a.url)} alt={a.name} className="h-28 w-28 object-cover" />
            </a>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {files.map((a, idx) => (
            <a
              key={idx}
              href={fileHref(a.url)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-1.5 text-sm hover:bg-muted transition w-fit max-w-full"
              data-testid={`${testId}-file-${idx}`}
            >
              <FileText size={16} className="text-primary shrink-0" />
              <span className="truncate">{a.name}</span>
              <DownloadSimple size={15} className="text-muted-foreground shrink-0" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
